import * as Immutable from 'immutable';
import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import {
  GroupClipSuggestion,
  GroupClipSuggestions,
} from 'redux/middleware/api/podcast-service';
import { groupClipSuggestionsSchema } from 'redux/middleware/api/podcast-service/schemas';
import { State } from 'redux/types';
import { DeepImmutableMap } from 'types';
import { entitiesSelector } from './base';

// selects the entire suggestion group
const normalizedSuggestionsGroupSelector = (state: State) =>
  entitiesSelector(state).getIn(['groupClipSuggestions', 'clipGroup']);

// selects the map of suggestions, keyed by their ids
export const groupClipSuggestionByIdSelector = (state: State) =>
  entitiesSelector(state).get('groupClipSuggestion');

// selects the denormalized version of suggestionsGroupSelector
export const groupClipSuggestionsSelector = createSelector(
  normalizedSuggestionsGroupSelector,
  groupClipSuggestionByIdSelector,
  (
    suggestionsGroup,
    clipSuggestionsById,
  ): DeepImmutableMap<GroupClipSuggestions> => {
    const entities = Immutable.Map({
      groupClipSuggestion: clipSuggestionsById,
    });

    return denormalize(suggestionsGroup, groupClipSuggestionsSchema, entities);
  },
);

export const groupClipSuggestionSelector = (
  id: number,
  state: State,
): GroupClipSuggestion => {
  const suggestions = groupClipSuggestionByIdSelector(state);
  return suggestions.get(String(id))?.toJS();
};

export const groupClipSuggestionsListSelector = createSelector(
  groupClipSuggestionsSelector,
  groupClipSuggestions =>
    !groupClipSuggestions
      ? []
      : (groupClipSuggestions
          .get('clipSuggestions', Immutable.List())
          .filter(suggestion => suggestion.get('status') !== 'failed')
          .toJS() as GroupClipSuggestion[]),
);

export const groupRequestIdSelector = createSelector(
  groupClipSuggestionsSelector,
  groupClipSuggestions =>
    !groupClipSuggestions
      ? undefined
      : groupClipSuggestions?.get('groupRequestId'),
);

export const pollGroupClipSuggestionsIdsSelector = createSelector(
  groupClipSuggestionsListSelector,
  groupClipSuggestions => {
    if (!groupClipSuggestions.length) {
      return undefined;
    }

    return groupClipSuggestions
      .filter(({ status }) => status === 'processing' || status === 'queued')
      .map(({ autoCreateEpisodeId }) => autoCreateEpisodeId);
  },
);

export const groupClipSuggestionsEddyProjectIdSelector = createSelector(
  groupClipSuggestionsSelector,
  suggestions =>
    suggestions?.get(
      'eddyProjectCuid',
    ) as GroupClipSuggestions['eddyProjectCuid'],
);
